"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ScoreConfigSchema = exports.validateNumericRangeFields = exports.TextConfigFields = exports.CategoricalConfigFields = exports.validateCategories = exports.BooleanConfigFields = exports.NumericConfigFields = exports.ScoreConfigNameSchema = exports.SCORE_CONFIG_NAME_MAX_LENGTH = exports.SCORE_CONFIG_NAME_MIN_LENGTH = exports.ScoreConfigCategory = void 0;
const zod_1 = require("zod");
const typeChecks_1 = require("../utils/typeChecks");
// Category type, used for categorical and boolean configs
exports.ScoreConfigCategory = zod_1.z.object({
    label: zod_1.z.string().min(1),
    value: zod_1.z.number(),
});
exports.SCORE_CONFIG_NAME_MIN_LENGTH = 1;
exports.SCORE_CONFIG_NAME_MAX_LENGTH = 80;
const SCORE_CONFIG_NAME_MAX_LENGTH_FOR_DB_READ = 80;
/** Input-only schema for score config names. Use at API/tRPC boundaries, not for DB reads. */
exports.ScoreConfigNameSchema = zod_1.z
    .string()
    .min(exports.SCORE_CONFIG_NAME_MIN_LENGTH)
    .max(exports.SCORE_CONFIG_NAME_MAX_LENGTH)
    .regex(/^[\p{L}\p{N}_ .()-]+$/u, "Name contains invalid characters");
// Numeric config fields
exports.NumericConfigFields = zod_1.z.object({
    maxValue: zod_1.z.number().nullish(),
    minValue: zod_1.z.number().nullish(),
    dataType: zod_1.z.literal("NUMERIC"),
    categories: zod_1.z.undefined().nullish(),
});
// Boolean config fields
exports.BooleanConfigFields = zod_1.z.object({
    dataType: zod_1.z.literal("BOOLEAN"),
    maxValue: zod_1.z.number().nullish(),
    minValue: zod_1.z.number().nullish(),
    categories: zod_1.z
        .array(exports.ScoreConfigCategory)
        .length(2, "Boolean data type must have exactly 2 categories.")
        .refine((categories) => {
        const expectedCategories = [
            { label: "True", value: 1 },
            { label: "False", value: 0 },
        ];
        return categories.every((category, index) => category.label === expectedCategories[index].label &&
            category.value === expectedCategories[index].value);
    }),
});
// Category config fields and types
const validateCategories = (categories, ctx) => {
    const uniqueNames = new Set();
    const uniqueValues = new Set();
    for (const category of categories) {
        if (uniqueNames.has(category.label)) {
            ctx.addIssue({
                code: "custom",
                message: `Duplicate category label: ${category.label}, category labels must be unique`,
            });
            return;
        }
        uniqueNames.add(category.label);
        if (uniqueValues.has(category.value)) {
            ctx.addIssue({
                code: "custom",
                message: `Duplicate category value: ${category.value}, category values must be unique`,
            });
            return;
        }
        uniqueValues.add(category.value);
    }
};
exports.validateCategories = validateCategories;
exports.CategoricalConfigFields = zod_1.z.object({
    maxValue: zod_1.z.undefined().nullish(),
    minValue: zod_1.z.undefined().nullish(),
    dataType: zod_1.z.literal("CATEGORICAL"),
    categories: zod_1.z.array(exports.ScoreConfigCategory).superRefine(exports.validateCategories),
});
exports.TextConfigFields = zod_1.z.object({
    maxValue: zod_1.z.undefined().nullish(),
    minValue: zod_1.z.undefined().nullish(),
    dataType: zod_1.z.literal("TEXT"),
    categories: zod_1.z.undefined().nullish(),
});
const ScoreConfigBase = zod_1.z.object({
    id: zod_1.z.string(),
    name: zod_1.z.string().min(1).max(SCORE_CONFIG_NAME_MAX_LENGTH_FOR_DB_READ),
    isArchived: zod_1.z.boolean(),
    description: zod_1.z.string().nullish(),
    createdAt: zod_1.z.coerce.date(),
    updatedAt: zod_1.z.coerce.date(),
    projectId: zod_1.z.string(),
});
const validateNumericRangeFields = (data, ctx) => {
    if (data.dataType === "NUMERIC") {
        if ((0, typeChecks_1.isPresent)(data.maxValue) &&
            (0, typeChecks_1.isPresent)(data.minValue) &&
            data.maxValue <= data.minValue) {
            ctx.addIssue({
                code: "custom",
                message: "Maximum value must be greater than Minimum value",
            });
        }
    }
};
exports.validateNumericRangeFields = validateNumericRangeFields;
exports.ScoreConfigSchema = zod_1.z
    .discriminatedUnion("dataType", [
    zod_1.z.object({
        ...ScoreConfigBase.shape,
        ...exports.NumericConfigFields.shape,
    }),
    zod_1.z.object({
        ...ScoreConfigBase.shape,
        ...exports.CategoricalConfigFields.shape,
    }),
    zod_1.z.object({
        ...ScoreConfigBase.shape,
        ...exports.BooleanConfigFields.shape,
    }),
    zod_1.z.object({
        ...ScoreConfigBase.shape,
        ...exports.TextConfigFields.shape,
    }),
])
    .superRefine(exports.validateNumericRangeFields);
